//stripefunction.js
import { auth } from "./config";

import { collection, addDoc, onSnapshot } from "firebase/firestore";
import { functions } from "./config";
import { httpsCallable } from "firebase/functions";

const FUNCTIONS_BASE = process.env.REACT_APP_FUNCTIONS_BASE;

// 🔹 Create a Stripe checkout session and return the URL
export const getCheckoutUrl = async (db, userId, priceId) => {
  const checkoutSessionRef = collection(
    db,
    "customers",
    userId,
    "checkout_sessions",
  );

  const sessionPayload = {
    price: priceId,
    success_url: `${window.location.origin}/subscription-success`,
    cancel_url: `${window.location.origin}/subscription`,
  };

  const docRef = await addDoc(checkoutSessionRef, sessionPayload);

  return new Promise((resolve, reject) => {
    const unsubscribe = onSnapshot(
      docRef,
      (snap) => {
        const data = snap.data();

        if (!data) return;

        const { error, url } = data;

        if (error) {
          unsubscribe();

          reject(
            new Error(
              error.message || "An error occurred while creating checkout.",
            ),
          );

          return;
        }

        if (url) {
          unsubscribe();
          resolve(url);
        }
      },
      (error) => {
        unsubscribe();

        reject(
          new Error(error?.message || "Failed to watch the checkout session."),
        );
      },
    );
  });
};

// 🔹 Redirect user to Stripe Billing Portal
export const getPortalUrl = async (userId) => {
  try {
    const functionRef = httpsCallable(
      functions,
      "ext-firestore-stripe-payments-createPortalLink",
    );

    const { data } = await functionRef({
      customerId: userId,
      returnUrl: `${window.location.origin}/settings`,
    });

    return data.url;
  } catch (error) {
    console.error("🔥 Error fetching Stripe portal URL:", error);
    throw new Error("Failed to open billing portal.");
  }
};

// 🔹 Swap the active subscription's price (in-app upgrade/downgrade)
export const updateSubscriptionPrice = async (
  targetPriceId,
  prorationDate = null,
) => {
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated");

  const idToken = await user.getIdToken();

  const res = await fetch(`${FUNCTIONS_BASE}/updateSubscriptionPrice`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ targetPriceId, prorationDate }),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || "Upgrade failed");
  return data;
};

export const previewSubscriptionChange = async (targetPriceId) => {
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated");

  const idToken = await user.getIdToken();

  const res = await fetch(`${FUNCTIONS_BASE}/previewSubscriptionChange`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ targetPriceId }),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || "Preview failed");
  return data;
};

// 🔹 Cancel subscription at end of billing period
export const cancelSubscriptionAtPeriodEnd = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated");

  const idToken = await user.getIdToken();

  const res = await fetch(`${FUNCTIONS_BASE}/cancelSubscriptionAtPeriodEnd`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || "Cancellation failed");
  return data; // { ok: true, cancelAt: unix }
};

// 🔹 Schedule a downgrade to be applied at end of current billing cycle
export const scheduleSubscriptionDowngrade = async (targetPriceId) => {
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated");

  const idToken = await user.getIdToken();

  const res = await fetch(`${FUNCTIONS_BASE}/scheduleSubscriptionDowngrade`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ targetPriceId }),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data?.error || "Downgrade failed");
  return data; // { ok, effectiveAt, teamSize, employeeLimit }
};
